import React, { useState, useEffect } from "react";
import "../../styles/Reports.scss";
import Modal from "react-modal";
import Select from "react-select";
import {
  getAllClients,
  getAllRecruiters,
  getStatusByReport,
  getUsersByRoleId
} from "../../services/api";
import ExcelJS from "exceljs";

Modal.setAppElement("#root");

const ITEMS_PER_PAGE = 10;

function StatusByReport() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Daily Report");

    // Add header row
    worksheet.columns = [
      { header: "S.NO", key: "sno" },
      { header: "Candidate Name", key: "candidate_name" },
      { header: "Client", key: "client_name" },
      { header: "L1 Stage with sub stages", key: "l1_status" },
      { header: "L2 Stage with sub stages", key: "l2_status" },
      { header: "Customer Discussion with sub stages", key: "client_status" },
      {
        header: "Recruiter",
        key: "recruiter_name",
      },
      // { header: "Candidates Joined", key: "candidates_joined" },
    ];

    // Add data rows
    reportData.forEach((item, index) => {
      worksheet.addRow({
        sno: index + 1,
        candidate_name: item.candidate_name || "-",
        client_name: item.client_name || "-",
        l1_status: item.l1_status || "-",
        l2_status: item.l2_status || "-",
        client_status: item.client_status || "-",
        recruiter_name: item.recruiter_name || "-",
      });
    });

    // Create a Blob from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "StatusByReport.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
  };

  // Reset to page 1 on search
  React.useEffect(() => {
    setPage(1);
  }, [selectedClient, startDate, endDate, reportData.length]);

 

  // for final summary filter along with pagination
  const filteredData = reportData;

  const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // To get All clients
  const fetchAllClients = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getAllClients();
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        setClients(res.result_info);
      } else {
        setError(res?.error_info || "Failed to fetch statuses");
        setClients([]);
      }
    } catch (err) {
      setError("Failed to fetch clients. Please try again.");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllClients()
  },[])

  // Fetch recruiters for role_id 4 (Recruiter)
    useEffect(() => {
      setLoading(true);
      const fetchRecruiters = async () => {
        setRecruiters([]);
        try {
          const res = await getAllRecruiters();
          if (res?.status === "S" && Array.isArray(res.result_info)) {
            setRecruiters(res.result_info);
          }
        } catch (error) {
          console.error("Error fetching recruiters:", error);
          setError("Failed to fetch recruiters");
        } finally {
          setLoading(false);
        }
      };
      fetchRecruiters();
    }, []);

  // To get final summary data
  const fetchStatusByReport = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getStatusByReport(
        selectedClient?.value || "",
        selectedRecruiter?.value || "",
        startDate,
        endDate
      );
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        setReportData(res.result_info);
      } else {
        setError(res?.message || "Failed to fetch report data");
        setReportData([]);
      }
    } catch (err) {
      setError("Failed to fetch report data. Please try again.");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };
  // Fetch report data when client or dates change
  useEffect(() => {
    fetchStatusByReport()
  }, [selectedClient, selectedRecruiter, startDate, endDate]);

  // Convert clients to react-select options
  const clientOptions = clients.map((client) => ({
    value: client.id,
    label: client.name,
  }));

  // Convert recruiters to react-select options
  const recruiterOptions = recruiters.map((recruiter) => ({
    value: recruiter.id,
    label: recruiter.name,
  }));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

   function getPageNumbers(currentPage, totalPages, maxPagesToShow = 5) {
    const pages = [];

    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(currentPage - half, 1);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxPagesToShow + 1, 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  return (
    <div className="reports-page">
      <div className="reports-header-row">
        <div className="reports-title-group">
          <h1>Status by Report</h1>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="export-btn" onClick={handleExport}>
            Export
          </button>
        </div>
      </div>
      <div className="reports-table-controls">
        <div className="filters-row">
          <div className="select-wrapper">
            <div className="d-flex align-items-center gap-2">
              <Select
                placeholder="Select a Client"
                value={selectedClient}
                onChange={setSelectedClient}
                options={clientOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: 220,
                    borderRadius: 6,
                    fontSize: "1rem",
                    background: "#fff",
                    border: "1.5px solid #d1d5db",
                  }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
                isClearable
                isSearchable
              />
              <Select
                placeholder="Select a Recruiter"
                value={selectedRecruiter}
                onChange={setSelectedRecruiter}
                options={recruiterOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: 220,
                    borderRadius: 6,
                    fontSize: "1rem",
                    background: "#fff",
                    border: "1.5px solid #d1d5db",
                  }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
                isClearable
                isSearchable
              />
            </div>
              
              
          </div>
          <div className="date-filters">
            <div className="date-input-wrapper">
              {!startDate && (
                <span className="placeholder-text">Start Date</span>
              )}
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => {
                  const newStartDate = e.target.value;
                  
                  if (endDate && newStartDate > endDate) {
                    // alert('Start date cannot be later than end date');
                    return;
                  }
                  
                  setStartDate(newStartDate);
                }}
                className="date-input"
              />
            </div>

            <div className="date-input-wrapper">
              {!endDate && <span className="placeholder-text">End Date</span>}
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => {
                  const newEndDate = e.target.value;
                  
                  if (startDate && newEndDate < startDate) {
                    // alert('End date cannot be earlier than start date');
                    return;
                  }
                  
                  setEndDate(newEndDate);
                }}
                className="date-input"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="reports-table-wrapper">
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading report data...
          </div>
        ) : (
          <table className="reports-table" data-columns="7">
            <thead>
              <tr>
                <th>S.NO</th>
                <th>Candidate Name</th>
                <th>Client</th>
                <th>L1 Stage with  sub stages</th>
                <th>L2 Stage with  sub stages</th>
                <th>Customer  discussion with   sub stages</th>
                <th>Recruiter</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    No reports found!
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.correl_id || index}>
                    <td>{(page - 1) * ITEMS_PER_PAGE + index + 1}</td>
                    <td>{item.candidate_name || "-"}</td>
                    <td>{item.client_name || "-"}</td>
                    <td>{item.l1_status || "-"}</td>
                    <td>{item.l2_status || "-"}</td>
                    <td>{item.client_status || "-"}</td>
                    <td>{item.recruiter_name || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination Controls */}
       {pageCount > 1 && (
  <div className="settings-pagination">
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page - 1)}
      disabled={page === 1}
    >
      &lt;
    </button>
    {getPageNumbers(page, pageCount, 5).map((pageNum) => (
      <button
        key={pageNum}
        className={"pagination-btn" + (page === pageNum ? " active" : "")}
        onClick={() => handlePageChange(pageNum)}
      >
        {pageNum}
      </button>
    ))}
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page + 1)}
      disabled={page === pageCount}
    >
      &gt;
    </button>
  </div>
)}
    </div>
  );
}

export default StatusByReport;
